import { useState } from "react";
import { Card, CardContent } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type { AttributeField, NodeField } from "@/features/properties/types/propertyAttributes";
import { getCategoryAccentClasses, getCategoryIcon } from "../propertyOverview/categoryIcons";
import { getCategoryAnchorId, getFieldAnchorId, type LeafField } from "./searchHelpers";
import { SimpleField } from "./fieldDisplays";

export function AttributeFieldRenderer({
  field,
  depth,
  defaultOpen = true,
}: {
  field: AttributeField;
  depth: number;
  defaultOpen?: boolean;
}) {
  if (field.type === "node") {
    return depth === 0 ? (
      <NodeSection node={field} depth={depth} defaultOpen={defaultOpen} />
    ) : (
      <SubSection node={field} depth={depth} />
    );
  }
  return <SimpleField field={field as LeafField} id={getFieldAnchorId(field)} />;
}

// Sub-section — lightweight grouping for nested nodes (depth >= 1).
// Rendered as a subtle uppercase label instead of a boxed card.
function SubSection({ node, depth }: { node: NodeField; depth: number }) {
  return (
    <div className={cn("pt-3", depth > 1 && "pl-3")}>
      <div className="mb-0.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground/70">
        <span>{node.title}</span>
        <span className="text-muted-foreground/40">· {node.fields.length}</span>
      </div>
      <div>
        {node.fields.map((child) => (
          <AttributeFieldRenderer key={child.id} field={child} depth={depth + 1} />
        ))}
      </div>
    </div>
  );
}

function NodeSection({
  node,
  depth,
  defaultOpen = true,
}: {
  node: NodeField;
  depth: number;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const Icon = depth === 0 ? getCategoryIcon(node.title) : null;
  const accentClasses = depth === 0 ? getCategoryAccentClasses(node.title) : "";

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Card
        id={depth === 0 ? getCategoryAnchorId(node) : undefined}
        className={cn(
          "border border-border/50 overflow-hidden scroll-mt-4",
          depth === 0
            ? "bg-card/50 backdrop-blur-sm"
            : "bg-card/30"
        )}
      >
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between p-4 hover:bg-accent/30 transition-colors text-left">
            <div className="flex items-center gap-2">
              {isOpen ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
              {Icon && (
                <span
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border",
                    accentClasses
                  )}
                >
                  <Icon className="h-4 w-4" />
                </span>
              )}
              <span
                className={cn(
                  "font-medium text-foreground",
                  depth === 0 ? "text-base" : "text-sm"
                )}
              >
                {node.title}
              </span>
            </div>
            <Badge variant="outline" className="text-xs text-muted-foreground">
              {node.fields.length} field{node.fields.length !== 1 ? "s" : ""}
            </Badge>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0 pb-3 px-4">
            {node.fields.map((child) => (
              <AttributeFieldRenderer
                key={child.id}
                field={child}
                depth={depth + 1}
                defaultOpen
              />
            ))}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
